/**
 * NEXUMA — RouteGuard Component
 *
 * Route-level permission guard for React Router v5.
 * Wraps a route and redirects to "/" (or renders AccessDenied)
 * if the current user doesn't have the required permission.
 *
 * Usage inside Switch:
 *   <RouteGuard
 *     path="/salarieslist"
 *     requires="payroll.view.company"
 *     component={SalariesList}
 *   />
 *
 *   <RouteGuard
 *     path="/audit-log"
 *     requiresAny={['audit_logs.view.company', 'audit_logs.view.all']}
 *   >
 *     <AuditLog />
 *   </RouteGuard>
 *
 * Props:
 *   path, exact, strict   — forwarded to <Route>
 *   component             — component to render (alternative to children)
 *   children              — children to render
 *   requires              — single permission
 *   requiresAny           — array of permissions (OR)
 *   requiresAll           — array of permissions (AND)
 *   requiresRole          — single role key
 *   requiresAnyRole       — array of role keys
 *   redirectTo            — redirect path on deny (default: null → shows AccessDenied)
 *   silent               — if true, redirect to "/" without showing AccessDenied
 */

import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import usePermissions from '../../hooks/usePermissions';
import AccessDenied from './AccessDenied';

export default function RouteGuard({
  path,
  exact        = false,
  strict       = false,
  component:   Component = null,
  children,
  requires        = null,
  requiresAny     = null,
  requiresAll     = null,
  requiresRole    = null,
  requiresAnyRole = null,
  redirectTo      = null,
  silent          = false,
}) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole, hasAnyRole } = usePermissions();

  function _check() {
    let granted = true;
    if (requires)            granted = granted && hasPermission(requires);
    if (requiresAny?.length) granted = granted && hasAnyPermission(requiresAny);
    if (requiresAll?.length) granted = granted && hasAllPermissions(requiresAll);
    if (requiresRole)        granted = granted && hasRole(requiresRole);
    if (requiresAnyRole?.length) granted = granted && hasAnyRole(requiresAnyRole);
    return granted;
  }

  return (
    <Route path={path} exact={exact} strict={strict}>
      {() => {
        const granted = _check();

        if (!granted) {
          if (redirectTo) return <Redirect to={redirectTo} />;
          if (silent)     return <Redirect to="/" />;
          return (
            <AccessDenied
              requiredPermission={
                requires || (requiresAny?.[0]) || null
              }
            />
          );
        }

        if (Component) return <Component />;
        return <>{children}</>;
      }}
    </Route>
  );
}
